import React from 'react';
import { Provider } from 'react-redux';
import { useBcpDxNextgenTouchpointFramework } from '@bcp-nextgen-dx-touchpoint-framework/react-js';
import { GetResourcesByListAndAreaOutputDtoSingleOutputMessage } from '@api/izibizi-xp';
import { Debug } from '@bcp-channels-sharedcomponents-reactjs/core';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	LoggerHelper,
	ShowHideHelper,
	TranslationHelper,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { Company, Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { ApiConfig, frameworkConfigs, LoggerConfig } from '@configs';
import { useLogger } from '@hooks';
import { TranslationValues } from '@constants';
import { GLOBAL_LOADER } from '@components';
import packageJson from '../package.json';
import { App } from './app';
import { Anything } from './types/common';
import { getSessionStorage } from './helpers/sessionHelper';
import { BaseStateSessionInfoGeoId } from './store/base';

export const TouchpointFrameworkBootstrap: React.FC = ({ children }): JSX.Element => {
	const logger = useLogger();
	const tokenOAuth = getSessionStorage('tokenOAuth');

	const { store, FrameworkProvider } = useBcpDxNextgenTouchpointFramework({
		...frameworkConfigs,
		journeyEngine: {
			...frameworkConfigs.journeyEngine,
			navigators: frameworkConfigs.journeyEngine.navigators.map((route) => {
				return {
					...route,
				};
			}),
		},
		communicationEngine: {
			...frameworkConfigs.communicationEngine,
			defaultHeaders: {
				...frameworkConfigs.communicationEngine.defaultHeaders,
				authorization: `Bearer ${tokenOAuth}`,
				'ocp-apim-subscription-key': ApiConfig.headers['ocp-apim-subscription-key'],
			},
		},
	});

	useDidMount(() => {
		const url =
			(process.env.REACT_APP_MOCK_START as string) === 'true'
				? (process.env.REACT_APP_API_URL as string)
				: ApiConfig.url;

		TranslationHelper.init({
			company: Company.bcp,
			language: Language.pt,
			siteName: TranslationValues.siteName,
			listName: TranslationValues.listName,
			keyGroup: TranslationValues.keyGroups.global,
			httpRequest: {
				url: `${url}api/rpc/RetrieveResourcesByListAndArea`,
				headers: {
					accept: ApiConfig.headers.accept,
					authorization: `Bearer ${tokenOAuth}`,
					'ocp-apim-subscription-key': ApiConfig.headers['ocp-apim-subscription-key'],
					'content-type': ApiConfig.headers['content-type'],
				} as Anything,
				loading: {
					hide: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > hideLoading');

						ShowHideHelper.hide(GLOBAL_LOADER);
					},
					show: (): void => {
						LoggerHelper.log('Translation', 'Translation > init > showLoading');

						ShowHideHelper.show(GLOBAL_LOADER);
					},
				},
				onError: (err: Anything): void => {
					LoggerHelper.log('Translation', 'Translation > init > treatResponseError', err);
					// ModalHelper.show(Modals.genericError);
				},
				onSuccess: (res: GetResourcesByListAndAreaOutputDtoSingleOutputMessage): Anything => {
					return TranslationHelper.transformArrayToObject(res.data?.list || [], {
						propNameKey: 'contentID',
						propNameValue: 'content',
					});
				},
				buildBody: (input): Anything => ({
					data: {
						languageAware: 'PT',
						listName: input.listName,
						siteName: input.siteName,
						screenID: input.keyGroup,
						resourceID: '',
						useCache: true,
						accountId: 0,
						metadata: {
							alias: '', //sessionInfo.alias,
							application: '14',
							geo: BaseStateSessionInfoGeoId.Lisbon,
							languageCode: 'PT',
							sessionId: '', //sessionInfo.sessionId,
							targetInfrastructure: '21', //sessionInfo.targetInfrastructure,
							targetSystem: 'PTCorporate', //sessionInfo.targetSystem,
						},
					},
					metadata: {
						messageId: '',
						messageCreatorId: '',
						messageCreationTimestampUTC: new Date(),
						correlationId: '',
						distributedTracingContext: '',
						authenticationValue: '',
						validateOnly: false,
						languageCode: '',
					},
				}),
			},
		});
	});

	logger('Touchpoint bootstrap');

	return (
		<>
			<FrameworkProvider>
				<Provider store={store}>
					<App />
				</Provider>
			</FrameworkProvider>
			{LoggerConfig.debug && <Debug location={window.location.pathname} version={packageJson.version} />}
		</>
	);
};
